import type { ParsedUrlQuery } from 'querystring'
import type { Product } from '@/types'

import { PRICE_RANGES, SORT_OPTIONS } from '@/constants/site'

export interface ShopFilters {
    q: string
    category: string | null
    price: string | null
    sort: string
    ready: boolean
}

export const DEFAULT_FILTERS: ShopFilters = { q: '', category: null, price: null, sort: 'featured', ready: false }

const single = (value: string | Array<string> | undefined): string | null => {
    if (Array.isArray(value)) return value[0] ?? null

    return typeof value === 'string' && value.length ? value : null
}

export const parseFilters = (query: ParsedUrlQuery): ShopFilters => {
    const sort = single(query.sort)
    const price = single(query.price)

    return {
        q: (single(query.q) ?? '').slice(0, 80),
        category: single(query.category),
        price: price && PRICE_RANGES.some(range => range.id === price) ? price : null,
        sort: sort && SORT_OPTIONS.some(option => option.id === sort) ? sort : 'featured',
        ready: single(query.ready) === '1'
    }
}

export const filtersToQuery = (filters: ShopFilters): Record<string, string> => {
    const query: Record<string, string> = {}

    if (filters.q.trim()) query.q = filters.q.trim()
    if (filters.category) query.category = filters.category
    if (filters.price) query.price = filters.price
    if (filters.sort !== 'featured') query.sort = filters.sort
    if (filters.ready) query.ready = '1'

    return query
}

const searchable = (product: Product) => {
    return `${product.name} ${product.tagline ?? ''} ${product.description ?? ''} ${product.category?.name ?? ''} ${product.colors.map(color => color.label).join(' ')} ${product.badge ?? ''}`.toLowerCase()
}

// Pure filtering so the server render and the browser always agree.
export const applyFilters = (products: Array<Product>, filters: ShopFilters): Array<Product> => {
    const terms = filters.q.toLowerCase().split(/\s+/).filter(Boolean)
    const range = PRICE_RANGES.find(entry => entry.id === filters.price) ?? null

    const filtered = products.filter(product => {
        if (filters.category && product.category?.slug !== filters.category) return false
        if (range && (product.price < range.min || (range.max !== null && product.price > range.max))) return false
        if (filters.ready && (product.madeToOrder || product.stock <= 0)) return false
        if (terms.length) {
            const text = searchable(product)

            if (!terms.every(term => text.includes(term))) return false
        }

        return true
    })

    const sorted = [...filtered]

    switch (filters.sort) {
        case 'price-asc':
            sorted.sort((a, b) => a.price - b.price)
            break
        case 'price-desc':
            sorted.sort((a, b) => b.price - a.price)
            break
        case 'name':
            sorted.sort((a, b) => a.name.localeCompare(b.name, 'en'))
            break
        case 'newest':
            sorted.sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : b.sortOrder - a.sortOrder))
            break
        default:
            sorted.sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured) || a.sortOrder - b.sortOrder)
    }

    return sorted
}
